import { getThreadScorerIdsForAgent } from '@fruitful/llm-service-shared';
import type { ClientScoreRowData } from '@mastra/client-js';
import { useEffect, useMemo, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodySmallEmphasized,
    BodySmallRegular,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { useListThreadScores } from '../hooks/useListThreadScores';
import { useLiveThreadRefetchInterval } from '../hooks/useLiveThreadRefetchInterval';
import {
    formatScoreCreatedAt,
    formatScorerValue,
    getScorerValueColor,
    scoresFromEarlierRuns,
    scoresFromLatestRun,
} from '../utils/scoreUtils';
import { ScorerValueWithRubric } from './ScorerValueWithRubric';

export const AiThreadScoresPanel = ({
    threadId,
    agentId,
    isRunningEval = false,
}: {
    threadId: string | null;
    agentId: string | null;
    isRunningEval?: boolean;
}) => {
    const refetchInterval = useLiveThreadRefetchInterval({
        additionalShouldPoll: isRunningEval,
    });

    const {
        data: scores = [],
        isLoading,
        error,
    } = useListThreadScores({
        threadId: threadId ?? undefined,
        refetchInterval,
        enabled: threadId != null,
    });

    const scorerIds = useMemo(
        () =>
            agentId != null && agentId !== ''
                ? getThreadScorerIdsForAgent(agentId)
                : [],
        [agentId],
    );

    const displayScores = useMemo(
        () => scoresFromLatestRun(scores, scorerIds),
        [scores, scorerIds],
    );

    const earlierScores = useMemo(
        () => scoresFromEarlierRuns(scores, displayScores, scorerIds),
        [scores, displayScores, scorerIds],
    );

    const [showEarlier, setShowEarlier] = useState(false);

    useEffect(() => {
        setShowEarlier(false);
    }, [threadId]);

    if (threadId == null || threadId === '') {
        return (
            <BodySmallRegular color="$gray700">
                Thread ID is required to view scores.
            </BodySmallRegular>
        );
    }

    const isBusy = isLoading || isRunningEval;

    return (
        <YStack width="100%" gap="$3">
            <YStack gap={2}>
                <BodyMediumEmphasized>Scores</BodyMediumEmphasized>
            </YStack>

            <YStack minHeight={160} position="relative">
                {error != null ? (
                    <BodySmallRegular color="$red1100">
                        Unable to load scores.
                    </BodySmallRegular>
                ) : displayScores.length === 0 ? (
                    isBusy ? null : (
                        <BodySmallRegular color="$gray600">
                            No scores yet. Use Run eval above to evaluate the
                            full conversation.
                        </BodySmallRegular>
                    )
                ) : (
                    <YStack gap="$3" opacity={isBusy ? 0.35 : 1}>
                        {displayScores.map((score) => (
                            <ScoreCard key={score.id} score={score} />
                        ))}
                        {earlierScores.length > 0 ? (
                            <YStack gap="$3">
                                <Button
                                    variant="tertiary"
                                    size="tiny"
                                    onPress={() => {
                                        setShowEarlier((prev) => !prev);
                                    }}
                                >
                                    {showEarlier
                                        ? 'Hide earlier evals'
                                        : `Show earlier evals (${earlierScores.length.toString()})`}
                                </Button>
                                {showEarlier ? (
                                    <YStack gap="$3">
                                        <BodySmallEmphasized color="$gray1000">
                                            Earlier evals
                                        </BodySmallEmphasized>
                                        {earlierScores.map((score) => (
                                            <ScoreCard
                                                key={score.id}
                                                score={score}
                                            />
                                        ))}
                                    </YStack>
                                ) : null}
                            </YStack>
                        ) : null}
                    </YStack>
                )}
                {isBusy ? (
                    <YStack
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="rgba(255,255,255,0.72)"
                    >
                        <Spinner />
                    </YStack>
                ) : null}
            </YStack>
        </YStack>
    );
};

const readUsefulnessScore = (value: unknown): number | null => {
    if (
        value == null ||
        typeof value !== 'object' ||
        !('usefulness' in value)
    ) {
        return null;
    }
    const usefulness = value.usefulness;
    if (
        usefulness == null ||
        typeof usefulness !== 'object' ||
        !('score' in usefulness)
    ) {
        return null;
    }
    const score = usefulness.score;
    return typeof score === 'number' &&
        Number.isInteger(score) &&
        score >= 1 &&
        score <= 5
        ? score
        : null;
};

const USEFULNESS_REASON_PREFIX = /^Usefulness \d\/5\.\s*/;

const reasonWithoutUsefulnessPrefix = (reason: string): string =>
    reason.replace(USEFULNESS_REASON_PREFIX, '');

const ScoreCard = ({ score }: { score: ClientScoreRowData }) => {
    const usefulness = readUsefulnessScore(score.analyzeStepResult);
    const rawDescription =
        typeof score.scorer.description === 'string'
            ? score.scorer.description.trim()
            : '';
    const scorerDescription =
        rawDescription === '' ||
        rawDescription.includes('not part of this percentage')
            ? null
            : rawDescription;
    const reason = reasonWithoutUsefulnessPrefix(score.reason?.trim() ?? '');

    return (
        <YStack
            gap="$2"
            padding="$3"
            borderRadius={12}
            borderWidth={1}
            borderColor="$gray200"
            backgroundColor="$gray100"
        >
            <XStack
                justifyContent="space-between"
                alignItems="flex-start"
                gap="$3"
            >
                <BodySmallEmphasized color="$gray1200" flex={1}>
                    {score.scorer.name}
                </BodySmallEmphasized>
                <ScorerValueWithRubric
                    score={score.score}
                    scorerId={score.scorerId}
                    withInfoButton
                >
                    <BodySmallEmphasized
                        color={getScorerValueColor({
                            score: score.score,
                            scorerId: score.scorerId,
                        })}
                        flexShrink={0}
                    >
                        {formatScorerValue({
                            score: score.score,
                            scorerId: score.scorerId,
                        })}
                    </BodySmallEmphasized>
                </ScorerValueWithRubric>
            </XStack>
            {scorerDescription != null ? (
                <BodySmallRegular color="$gray1000">
                    {scorerDescription}
                </BodySmallRegular>
            ) : null}
            <BodySmallRegular color="$gray1000">
                Evaluated {formatScoreCreatedAt(score)}
            </BodySmallRegular>
            {usefulness != null ? (
                <BodySmallRegular color="$gray1200">
                    Usefulness {usefulness.toString()}/5
                </BodySmallRegular>
            ) : null}
            <BodySmallRegular color="$gray1200">
                {reason !== '' ? reason : 'No reason provided'}
            </BodySmallRegular>
        </YStack>
    );
};
